import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { BarChart2, Trash2, ChevronRight } from 'lucide-react'
import { savedReportApi } from '../../lib/api/savedReports'
import { SavedReport } from '../../types/reports'

export function SavedReportsListPage() {
  const navigate = useNavigate()

  const [savedReports, setSavedReports] = useState<SavedReport[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [reportToDelete, setReportToDelete] = useState<SavedReport | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        const data = await savedReportApi.list()
        setSavedReports(data)
      } catch {
        toast.error('Failed to load saved reports')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [])

  const handleDelete = async () => {
    if (!reportToDelete) return
    setIsDeleting(true)
    try {
      await savedReportApi.destroy(reportToDelete.id)
      setSavedReports(prev => prev.filter(r => r.id !== reportToDelete.id))
      toast.success('Report deleted')
      setReportToDelete(null)
    } catch {
      toast.error('Failed to delete report')
    } finally {
      setIsDeleting(false)
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950 px-6 py-6">
      <div className="max-w-screen-xl mx-auto">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Saved Reports</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Load a previously saved report configuration
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : savedReports.length === 0 ? (
          <div className="mt-8 text-center py-16 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
            <div className="w-14 h-14 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mx-auto mb-4">
              <BarChart2 className="w-7 h-7 text-primary-500" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">No saved reports yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Run a report and click <strong>Save</strong> to save it here
            </p>
            <button
              onClick={() => navigate('/reports')}
              className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              New Report
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {savedReports.map(report => (
                <div
                  key={report.id}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                >
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {report.name}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {report.advertiser && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {report.advertiser.name}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        Saved {formatDate(report.created_at)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setReportToDelete(report)
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete report"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {reportToDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isDeleting && setReportToDelete(null)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Report</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">"{reportToDelete.name}"</span>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReportToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
